import { isOfflineEnabled } from '../db/odin-db-util';

type Subscriber = (isOnline: boolean) => void;

//  check to see if we are in a browser environment.
const isBrowser = typeof window !== 'undefined' && typeof navigator !== 'undefined';

/**
 * A singleton service that tracks the browser's current online/offline status and notifies subscribers
 * whenever connectivity changes (listens to the window 'online' and 'offline' events).
 *
 * Subscribers receive an immediate synchronous emission of the current status upon subscribing,
 * followed by updates on each connectivity change.
 *
 * @remarks
 * - Uses `navigator.onLine` for initial state.
 * - Cleans up individual subscribers via the returned unsubscribe function.
 */
class OnlineStatusService {
  private isOnline: boolean = true;
  private subscribers: Set<Subscriber> = new Set();

  constructor() {
    if (!isBrowser || !isOfflineEnabled) return;

    this.isOnline = navigator.onLine;
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  private handleOnline = () => {
    this.isOnline = true;
    this.notifySubscribers();
  };

  private handleOffline = () => {
    this.isOnline = false;
    this.notifySubscribers();
  };

  private notifySubscribers() {
    this.subscribers.forEach((callback) => callback(this.isOnline));
  }

  /**
   * Get the current online connectivity status.
   *
   * @returns True if the service is online; otherwise, false.
   */
  public getStatus = (): boolean => {
    return this.isOnline;
  };

  /**
   * Subscribes to online status changes.
   *
   * Immediately invokes the provided callback with the current status
   * and on each subsequent status change.
   *
   * @param callback Function invoked with the current online status (true = online, false = offline).
   *
   * @returns Unsubscribe function to stop receiving updates.
   */
  public subscribe(callback: Subscriber): () => void {
    this.subscribers.add(callback);

    // Immediately call the callback with the current status
    callback(this.isOnline);

    // Return an unsubscribe function
    return () => {
      this.subscribers.delete(callback);
    };
  }
}

// Export a singleton instance of the service
export const onlineStatusService = new OnlineStatusService();
